import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { usePlaylistFeatureFlag } from 'featureFlags';
import {
  ChakraFabButton,
  ChakraPageTitle,
  ContentContainer,
} from 'app/components/Core';
import { ContentContainerType } from 'types/enums';
import { PlaylistAudioSelectOverlay } from 'app/components/Playlist/PlaylistAudioSelectOverlay';
import { PlaylistsList } from 'app/components/Playlist/PlaylistsList';
import { useAppDispatch, useAppSelector } from 'app/store';
import { fetchPlaylistsEffect } from 'app/store/Playlists/effects';
import { getPlaylistsList } from 'app/store/Playlists/selectors';
import { setShowUpgradePopup } from 'app/store/Storage/slice';
import { useCreatePlaylistLimit } from 'app/components/Playlist/hooks';

export const Playlists: React.FC = () => {
  const dispatch = useAppDispatch();
  const { playlistFeatureEnabled } = usePlaylistFeatureFlag();
  const playlists = useAppSelector(getPlaylistsList);
  const [openOverlay, setOpenOverlay] = useState(false);
  const [isListLoading, setIsListLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { state } = useLocation();
  const shouldShowPopup = state?.shouldShowPopup;
  const { isLimitReached } = useCreatePlaylistLimit();

  const fetchPlaylists = useCallback(async () => {
    try {
      await dispatch(fetchPlaylistsEffect());
    } finally {
      setIsListLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (shouldShowPopup) {
      navigate('', { state: { shouldShowPopup: undefined } });
      dispatch(
        setShowUpgradePopup({
          messageType: 'playlist',
          context: { action: 'createPlaylist' },
        })
      );
    } else if (playlistFeatureEnabled) {
      fetchPlaylists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowPopup]);

  const onFabButtonClick = () => {
    if (isLimitReached) {
      dispatch(
        setShowUpgradePopup({
          messageType: 'playlist',
          context: { action: 'createPlaylist' },
        })
      );
    } else {
      setOpenOverlay(true);
    }
  };

  if (!playlistFeatureEnabled) {
    return <Navigate to="/" />;
  }

  return (
    <ContentContainer
      type={ContentContainerType.LIST}
      isViewContent
      paddingTop={8}
      paddingBottom={24}
      height="full"
    >
      <ChakraPageTitle pb={8}>Playlists</ChakraPageTitle>
      <PlaylistsList playlists={playlists} loading={isListLoading} />
      <ChakraFabButton
        aria-label="new playlist"
        data-testid="new-playlist"
        onClick={onFabButtonClick}
        leftIcon={<AddIcon />}
      >
        New Playlist
      </ChakraFabButton>
      {openOverlay && (
        <PlaylistAudioSelectOverlay onClose={() => setOpenOverlay(false)} />
      )}
    </ContentContainer>
  );
};
